import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Facebook, Instagram } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

export const Footer = () => {
  const [email, setEmail] = useState("");

  const subscribe = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setEmail("");
    toast.success("You're on the list — hope is headed to your inbox.");
  };

  return (
    <footer data-testid="site-footer" className="bg-forest text-paper">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-24 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8 pb-16 border-b border-paper/15">
          <div className="md:col-span-5">
            <Logo dark testid="footer-logo" />
            <p className="mt-6 text-paper/70 leading-relaxed max-w-sm">
              Standing in the gap for children in need — feeding and educating communities in the Philippines and beyond.
            </p>
            <p className="mt-6 flex items-start gap-2 text-sm text-paper/60">
              <MapPin size={16} className="mt-0.5 shrink-0" />
              2620 Apodaca Rd, Las Cruces, NM 88005
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a
                href="https://www.facebook.com/HopeCentersIntl"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-social-facebook"
                aria-label="Hope Centers on Facebook"
                className="grid place-items-center w-11 h-11 rounded-full border border-paper/20 text-paper/70 transition-[color,border-color,transform] duration-300 hover:text-marigold hover:border-marigold hover:scale-105"
              >
                <Facebook size={18} />
              </a>
              <a
                href="https://www.instagram.com/hopecenters"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-social-instagram"
                aria-label="Hope Centers on Instagram"
                className="grid place-items-center w-11 h-11 rounded-full border border-paper/20 text-paper/70 transition-[color,border-color,transform] duration-300 hover:text-marigold hover:border-marigold hover:scale-105"
              >
                <Instagram size={18} />
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-paper/50">Explore</p>
            <ul className="mt-5 space-y-3">
              {[
                { to: "/programs", label: "Programs" },
                { to: "/about", label: "About us" },
                { to: "/golf-scramble", label: "Events" },
                { to: "/contact", label: "Contact" },
                { to: "/donate", label: "Donate" },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="text-paper/80 transition-colors duration-300 hover:text-marigold"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-paper/50">Letters of hope</p>
            <p className="mt-5 text-paper/70 text-sm leading-relaxed">
              One short email a month — stories from the centers, never spam.
            </p>
            <form onSubmit={subscribe} className="mt-5 flex gap-2" data-testid="footer-newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.org"
                data-testid="footer-newsletter-input"
                className="flex-1 min-w-0 bg-transparent border-b border-paper/30 py-3 text-paper placeholder:text-paper/40 outline-none transition-colors duration-300 focus:border-marigold"
              />
              <button
                type="submit"
                data-testid="footer-newsletter-submit"
                aria-label="Subscribe"
                className="grid place-items-center w-12 h-12 rounded-full bg-terra text-paper transition-[background-color,transform] duration-300 hover:bg-terra-dark hover:scale-105"
              >
                <ArrowUpRight size={18} />
              </button>
            </form>
          </div>
        </div>

        <div className="pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-paper/50">
          <p>
            © {new Date().getFullYear()} Hope Centers International · 2620 Apodaca Rd, Las Cruces, NM 88005 · Hope
            Centers LLC is a registered 501(c)(3). All donations are tax deductible in full or in part.
          </p>
          <p className="font-mono uppercase tracking-[0.2em]">Full plates · Open books · Bright futures</p>
        </div>
      </div>
    </footer>
  );
};
